import { useState, type ReactNode } from 'react';
import { getApp, initializeApp } from 'firebase/app';
import { getAuth, signInWithPopup, type AuthProvider } from 'firebase/auth';
import { AppLogo } from '@/components/icons/AppLogo';

const DEEP_LINK_SCHEME = 'crownprediction://auth';
const API_BASE_URL = import.meta.env.VITE_API_URL;

const FIREBASE_CONFIG = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

export interface NativeSignInPageProps {
  appName: string;
  providerLabel: string;
  createProvider: () => AuthProvider;
  buttonClassName: string;
  buttonIcon: ReactNode;
  idleIcon: ReactNode;
  idleGlowClass: string;
  idleBorderClass: string;
  idleBgClass: string;
  loadingGlowClass?: string;
  loadingBorderClass?: string;
  loadingBgClass?: string;
  loadingSpinnerClass?: string;
}

/**
 * Dedicated Firebase app — separate from the default app that AuthClient
 * initializes with App Check (reCAPTCHA v3). App Check attestation can fail in the
 * system browser context, causing "auth/network-request-failed". A clean named app
 * without App Check avoids this.
 */
function getFirebaseAuth(appName: string) {
  let app;
  try {
    app = getApp(appName);
  } catch {
    app = initializeApp(FIREBASE_CONFIG, appName);
  }
  return getAuth(app);
}

type Status = 'idle' | 'loading' | 'success' | 'error';

/**
 * Shared native sign-in page opened by the React Native app in the **system browser**
 * (not the WebView) to work around OAuth providers blocking sign-in inside WebViews.
 *
 * Shows a "Continue with {provider}" button that triggers `signInWithPopup` — the popup
 * must come from a real user tap because:
 * - Browsers block popups from non-interactive contexts (useEffect, timers)
 * - `signInWithRedirect` is broken in Safari (ITP clears sessionStorage across redirects)
 *
 * On success: `getIdToken()` → `POST /auth/login` → deep link with credentials.
 */
export function NativeSignInPage({
  appName,
  providerLabel,
  createProvider,
  buttonClassName,
  buttonIcon,
  idleIcon,
  idleGlowClass,
  idleBorderClass,
  idleBgClass,
  loadingGlowClass,
  loadingBorderClass,
  loadingBgClass,
  loadingSpinnerClass,
}: NativeSignInPageProps) {
  const [status, setStatus] = useState<Status>('idle');
  const [error, setError] = useState<string | null>(null);

  const glowClass = status === 'loading' ? (loadingGlowClass ?? idleGlowClass) : idleGlowClass;
  const borderClass = status === 'loading' ? (loadingBorderClass ?? idleBorderClass) : idleBorderClass;
  const bgClass = status === 'loading' ? (loadingBgClass ?? idleBgClass) : idleBgClass;
  const spinnerClass = loadingSpinnerClass ?? 'text-accent-dark';

  async function runSignIn() {
    setStatus('loading');
    setError(null);

    try {
      const provider = createProvider();
      const credential = await signInWithPopup(getFirebaseAuth(appName), provider);
      const firebaseIdToken = await credential.user.getIdToken();

      // Hand-rolled fetch: this page renders outside AuthShell / QueryClientProvider,
      // so the Orval-generated hooks are unavailable. Not a pattern to copy elsewhere.
      const res = await fetch(`${API_BASE_URL}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ firebaseIdToken }),
      });

      if (!res.ok) {
        const body = (await res.json().catch(() => null)) as { message?: string } | null;
        throw new Error(body?.message ?? `Login failed (${res.status})`);
      }

      const { accessToken, profile } = (await res.json()) as {
        accessToken: string;
        profile: Record<string, unknown>;
      };

      const json = JSON.stringify({ accessToken, profile });
      const payload = btoa(new TextEncoder().encode(json).reduce((s, b) => s + String.fromCharCode(b), ''));
      setStatus('success');

      window.location.href = `${DEEP_LINK_SCHEME}?token=${encodeURIComponent(payload)}`;
    } catch (err) {
      // Popup blocked by browser — show the button as fallback.
      if (err instanceof Error && err.message.includes('auth/popup-blocked')) {
        setStatus('idle');
        return;
      }

      // Popup closed by user
      if (err instanceof Error && err.message.includes('popup-closed-by-user')) {
        setStatus('idle');
        setError('Sign-in was cancelled.');
        return;
      }

      setStatus('error');
      setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
    }
  }

  return (
    <div className="relative flex min-h-svh flex-col items-center justify-center overflow-hidden px-6 py-10">
      <NativeSignInKeyframes />

      {/* Atmospheric brand glows */}
      <span
        aria-hidden
        className="pointer-events-none absolute -top-24 -right-20 size-72 rounded-full bg-accent/25 blur-[90px]"
      />
      <span
        aria-hidden
        className="pointer-events-none absolute -bottom-28 -left-24 size-80 rounded-full bg-purple/20 blur-[100px]"
      />

      <div className="mb-8" style={{ animation: 'nsi-fade-up 0.5s ease-out both' }}>
        <AppLogo width={150} />
      </div>

      <div
        className="relative w-full max-w-[400px] rounded-[24px] border border-border bg-surface px-7 py-10 shadow-card"
        style={{ animation: 'nsi-fade-up 0.6s ease-out 0.06s both' }}
      >
        <div className="flex flex-col items-center text-center" role="status" aria-live="polite">
          <StatusIndicator
            status={status}
            idleIcon={idleIcon}
            glowClass={glowClass}
            borderClass={borderClass}
            bgClass={bgClass}
            spinnerClass={spinnerClass}
          />

          <h1
            className="mt-7 text-[26px] font-bold leading-tight text-primary"
            style={{ animation: 'nsi-fade-up 0.5s ease-out 0.16s both' }}
          >
            {status === 'success'
              ? 'Signed in'
              : status === 'error'
                ? 'Sign-in failed'
                : status === 'loading'
                  ? 'Signing in…'
                  : `Sign in with ${providerLabel}`}
          </h1>

          <p
            className="mt-3 max-w-[300px] text-[15px] leading-relaxed text-muted"
            style={{ animation: 'nsi-fade-up 0.5s ease-out 0.22s both' }}
          >
            {status === 'success'
              ? 'Redirecting you back to the app…'
              : status === 'error'
                ? error
                : status === 'loading'
                  ? `Complete the ${providerLabel} sign-in to continue.`
                  : error
                    ? error
                    : 'Tap the button below to continue.'}
          </p>

          {(status === 'idle' || status === 'error') && (
            <div className="mt-8 w-full" style={{ animation: 'nsi-fade-up 0.5s ease-out 0.28s both' }}>
              <button
                type="button"
                onClick={() => void runSignIn()}
                className={`inline-flex w-full items-center justify-center gap-2.5 rounded-pill px-6 py-3.5 text-[15px] font-semibold text-white shadow-card transition-opacity active:opacity-80 ${buttonClassName}`}
              >
                {buttonIcon}
                {status === 'error' ? 'Try again' : `Continue with ${providerLabel}`}
              </button>
            </div>
          )}
        </div>
      </div>

      <p className="mt-6 text-[13px] text-muted" style={{ animation: 'nsi-fade-up 0.5s ease-out 0.34s both' }}>
        Crown Prediction
      </p>
    </div>
  );
}

/** Visual indicator for the current auth flow state. */
function StatusIndicator({
  status,
  idleIcon,
  glowClass,
  borderClass,
  bgClass,
  spinnerClass,
}: {
  status: Status;
  idleIcon: ReactNode;
  glowClass: string;
  borderClass: string;
  bgClass: string;
  spinnerClass: string;
}) {
  if (status === 'success') {
    return (
      <Badge glowClass="bg-success/40" ringClass="border-2 border-success/40 bg-success/10">
        <svg viewBox="0 0 24 24" fill="none" className="size-11 text-success" aria-hidden>
          <path
            d="M4.5 12.5l5 5 10-11"
            stroke="currentColor"
            strokeWidth="2.4"
            strokeLinecap="round"
            strokeLinejoin="round"
            pathLength={1}
            style={{
              strokeDasharray: 1,
              strokeDashoffset: 1,
              animation: 'nsi-draw 0.55s ease-out 0.18s forwards',
            }}
          />
        </svg>
      </Badge>
    );
  }

  if (status === 'error') {
    return (
      <Badge glowClass="bg-danger/35" ringClass="border-2 border-danger/40 bg-danger/10">
        <svg viewBox="0 0 24 24" fill="none" className="size-10 text-danger" aria-hidden>
          <path d="M12 8v5" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" />
          <circle cx="12" cy="16.5" r="1.3" fill="currentColor" />
          <path d="M12 3.5l9 15.5H3l9-15.5z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
        </svg>
      </Badge>
    );
  }

  if (status === 'loading') {
    return (
      <div className="relative grid place-items-center" style={{ animation: 'nsi-pop 0.5s ease-out both' }}>
        <span
          aria-hidden
          className={`absolute inset-0 rounded-full blur-2xl ${glowClass}`}
          style={{ animation: 'nsi-glow 3s ease-in-out infinite' }}
        />
        <div className={`relative grid size-24 place-items-center rounded-full border-2 ${borderClass} ${bgClass}`}>
          <svg viewBox="0 0 24 24" fill="none" className={`size-9 animate-spin ${spinnerClass}`} aria-hidden>
            <circle cx="12" cy="12" r="9" stroke="currentColor" strokeOpacity="0.2" strokeWidth="2.4" />
            <path d="M21 12a9 9 0 0 0-9-9" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" />
          </svg>
        </div>
      </div>
    );
  }

  // idle — provider icon
  return (
    <div className="relative grid place-items-center" style={{ animation: 'nsi-pop 0.5s ease-out both' }}>
      <span
        aria-hidden
        className={`absolute inset-0 rounded-full blur-2xl ${glowClass}`}
        style={{ animation: 'nsi-glow 3s ease-in-out infinite' }}
      />
      <div className={`relative grid size-24 place-items-center rounded-full border-2 ${borderClass} ${bgClass}`}>
        {idleIcon}
      </div>
    </div>
  );
}

function Badge({ glowClass, ringClass, children }: { glowClass: string; ringClass: string; children: ReactNode }) {
  return (
    <div className="relative grid place-items-center" style={{ animation: 'nsi-pop 0.5s ease-out both' }}>
      <span
        aria-hidden
        className={`absolute inset-0 rounded-full blur-2xl ${glowClass}`}
        style={{ animation: 'nsi-glow 3s ease-in-out infinite' }}
      />
      <div className={`relative grid size-24 place-items-center rounded-full ${ringClass}`}>{children}</div>
    </div>
  );
}

/** Scoped keyframes — CSS-only animations shared by all native sign-in pages. */
function NativeSignInKeyframes() {
  return (
    <style>{`
      @keyframes nsi-fade-up { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: none; } }
      @keyframes nsi-pop { 0% { opacity: 0; transform: scale(0.6); } 60% { opacity: 1; transform: scale(1.08); } 100% { transform: scale(1); } }
      @keyframes nsi-glow { 0%, 100% { opacity: 0.45; transform: scale(1); } 50% { opacity: 0.8; transform: scale(1.1); } }
      @keyframes nsi-draw { to { stroke-dashoffset: 0; } }
      @media (prefers-reduced-motion: reduce) {
        [style*="nsi-"] { animation: none !important; }
      }
    `}</style>
  );
}
